import type { AccountPolkadot } from '@pkg/database-portfolio'
import { type Chain, getCurrentFinalizedBlockNumber } from '@pkg/polkadot'
import { retryWith401Handler } from '../utils'

/** 認証 token が取得できない場合に利用する空の request header */
const emptyHeaders: Record<string, string> = {}

/** Subscan free plan で history_window_exceeded を避けるための最大履歴日数 */
const subscanFreePlanHistoryDays = 28 * 3

/** 1 日あたりの秒数 */
const secondsPerDay = 24 * 60 * 60

type GetAddressEventsParameters = Parameters<
  Chain['explorer']['getAddressEvents']
>[0]
type OnFetchedParameters = Parameters<
  NonNullable<GetAddressEventsParameters['onFetched']>
>[0]

/**
 * chain の block time から Subscan free plan で取得可能な block window を計算する
 * @param params - 対象 chain
 * @returns Subscan に指定する最大 block range 幅
 */
const getSubscanFreePlanHistoryBlockWindow = (params: {
  chain: Chain
}): number => {
  const { blockTimeSeconds } = params.chain

  if (!Number.isFinite(blockTimeSeconds) || blockTimeSeconds <= 0) {
    throw new Error(`Invalid chain block time: ${blockTimeSeconds}`)
  }

  return Math.floor(
    (subscanFreePlanHistoryDays * secondsPerDay) / blockTimeSeconds,
  )
}

/**
 * Polkadot explorer から account の event index を取得する
 * @param params - 対象 account と chain と認証 token 取得処理
 * @returns account に紐づく event index 一覧
 */
export const getEventIndexes = async (params: {
  account: AccountPolkadot
  chain: Chain
  getToken?: () => Promise<string | null>
  onFetched?: (params: OnFetchedParameters) => Promise<void>
}) => {
  const { account, chain, getToken, onFetched } = params
  const fromBlock = (account.polkadotToBlock ?? 0) + 1
  const toBlock = await getCurrentFinalizedBlockNumber({ chain })
  const subscanFreePlanHistoryBlockWindow =
    getSubscanFreePlanHistoryBlockWindow({ chain })
  const windowFromBlock = Math.max(
    1,
    toBlock - subscanFreePlanHistoryBlockWindow + 1,
  )
  const adjustedFromBlock = Math.max(fromBlock, windowFromBlock)

  if (fromBlock > toBlock) {
    await onFetched?.({ events: [], toBlock, completed: true })
    return []
  }

  return await retryWith401Handler({
    fn: async () =>
      await chain.explorer.getAddressEvents({
        address: account.polkadotAddress,
        fromBlock: adjustedFromBlock,
        toBlock,
        onFetched,
        createHeaders:
          getToken === undefined
            ? undefined
            : async () => {
                const token = await getToken()
                if (token === null) {
                  return emptyHeaders
                }
                return {
                  Authorization: `Bearer ${token}`,
                }
              },
      }),
  })
}
